import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { RankmiTable } from '@rankmi/rankmi-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisV, faPlus, faTimes } from '@fortawesome/pro-solid-svg-icons'
import { Button as AntButton, Col, Dropdown, Input, Menu, Row } from 'antd'
import { EnrolState } from 'enum'
import Tag from 'components/ui/tag/Tag'
import styles from 'components/configuration/components/users/components/add-participants-modal/index.module.scss'
import UserAvatar from 'components/ui/user-avatar/UserAvatar'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/participants.action'
import Button, { ButtonSizes } from 'design-system/button'
import { IAreaModel } from 'models/area.model'
import { ILmsProfileModel } from 'models/lmsProfile.model'
import createManualAssignation from 'api/administration/applications/AssignManually'

interface IInviteParticipants {
  show: boolean
  onClose: any
  courseId: string | number
  resetFilters: any
  refetch?(): void | Promise<void>
}

const InviteParticipants: React.FC<IInviteParticipants> = ({
  show,
  onClose,
  resetFilters,
  courseId,
  refetch,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [dataSource, setDatasource] = useState([] as any)
  const auxSelectedRows = useRef([])
  const [page, setPage] = useState(1)
  const [selectedRows, setSelectedRows] = useState([] as any)
  const [sort, setSort] = useState({})
  const [onSearch, setOnSearch] = useState('')

  const prefix = 'configuration.users.enableParticipantsModal'

  const { list, total, loading, inactiveParticipantsListLoading } = useSelector(
    ({ participants }: IMainState) => participants
  )
  const debounceTimeOut = useRef<number>()

  const handleManualAssignmentUsers = useCallback(
    async (ids: number[]) => {
      const difference = auxSelectedRows.current.filter(
        (key: number) => !ids.includes(key)
      )
      setSelectedRows(difference)
      await createManualAssignation(
        {
          state: EnrolState.Approved,
          stateAdmin: EnrolState.Approved,
          courseId: +courseId,
          manuallyAssigned: true,
          mandatory: true,
        },
        ids
      )
      resetFilters()
      setOnSearch('')
      refetch?.()
      onClose()
    },
    [resetFilters, refetch, courseId, onClose]
  )

  const columns = [
    {
      title: t(`${prefix}.table.headers.name`),
      dataIndex: 'name',
      key: 'name',
      render: ({ avatar, fullName }: any) => (
        <UserAvatar user={{ fullName, avatar }} className={styles.userData} />
      ),
      sorteable: true,
    },
    {
      title: t(`${prefix}.table.headers.identifier`),
      dataIndex: 'identifier',
      key: 'identifier',
      sorteable: true,
    },
    {
      title: t(`${prefix}.table.headers.position`),
      dataIndex: 'position',
      key: 'position',
      sorteable: true,
    },
    {
      title: t(`${prefix}.table.headers.area`),
      dataIndex: 'area.name',
      key: 'area.name',
    },
    {
      title: t('administration.monitor.participantResults.lmsProfiles'),
      dataIndex: 'lmsProfiles',
      key: 'lmsProfiles',
      width: '200px',
    },
  ]

  const generateAreas = (userAreas: IAreaModel[]) => {
    return userAreas.map(({ name }) => name).join(', ')
  }

  const handleSearch = (value: string) => {
    setOnSearch(value)
    setPage(1)
  }

  const handleInviteUsersBulk = () => {
    handleManualAssignmentUsers(selectedRows)
  }

  const debounce = useCallback(
    (page, sort, onSearch) => {
      window.clearTimeout(debounceTimeOut.current)
      debounceTimeOut.current = window.setTimeout(
        () =>
          dispatch({
            type: fromActions.PARTICIPANTS_PROFILES_LIST,
            payload: { page, sort, searchText: onSearch },
          }),
        100
      )
    },
    [dispatch]
  )

  useEffect(() => {
    debounce(page, sort, onSearch)
  }, [debounce, page, sort, onSearch])

  useEffect(() => {
    setDatasource(
      list.map(
        ({
          fullName,
          avatar,
          identifier,
          area,
          position,
          id,
          lmsProfiles,
        }) => ({
          key: id,
          name: { fullName, avatar },
          identifier,
          'area.name': area ? generateAreas([area]) : '',
          position,
          lmsProfiles: (
            <div style={{ display: 'inline-block' }}>
              {lmsProfiles &&
                lmsProfiles.map((profile: ILmsProfileModel, index) => {
                  if (index < 3) {
                    return (
                      <Tag
                        key={profile.name + '+' + profile.color}
                        text={profile.name}
                        color={profile.color}
                        size={'sm'}
                      />
                    )
                  }
                })}
              {lmsProfiles && lmsProfiles.length > 3 && (
                <Tag
                  key={lmsProfiles.length}
                  text={`+${lmsProfiles.length - 3}`}
                  color={''}
                  size={'sm'}
                />
              )}
            </div>
          ),
        })
      )
    )
  }, [list])

  useEffect(() => {
    auxSelectedRows.current = selectedRows
  }, [selectedRows])

  const onHide = () => {
    setOnSearch('')
    return onClose()
  }

  return (
    <Modal
      size="lg"
      show={show}
      onHide={onHide}
      className={styles.modalContainer}
    >
      <Modal.Body className={'pt-0 px-0'}>
        <Row className="position-relative">
          <h1>{t('administration.monitor.inviteParticipantsModal.title')}</h1>
          <a className={styles.closeButton} onClick={onHide}>
            <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
          </a>
        </Row>
        <Row className="position-relative mt-3 mb-2" gutter={10}>
          <Col xs={16}>
            <Input.Search
              placeholder={t(`${prefix}.searchParticipants`)}
              className={styles.search}
              onChange={(e: any) => handleSearch(e.target.value)}
            />
          </Col>
          <Col xs={8} className="d-flex ">
            <Button
              onClick={() => !!selectedRows.length && handleInviteUsersBulk()}
              className={`${styles.actionButton} w-100`}
              disabled={!selectedRows.length}
              size={ButtonSizes.Small}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              {t(`configuration.users.inviteParticipants.inviteSelected`)}
            </Button>
            <Dropdown
              className={'d-none'}
              placement="bottomRight"
              overlay={
                <Menu>
                  <Menu.Item key="1" disabled={true}>
                    {t(`${prefix}.actions.addAll`)}
                  </Menu.Item>
                </Menu>
              }
            >
              <AntButton
                className={`${styles.actionButton} ${styles.buttonSecondary} ml-2`}
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </AntButton>
            </Dropdown>
          </Col>
        </Row>
        <Row>
          <RankmiTable
            data={dataSource}
            total={total}
            columnHeaders={columns}
            onSortChange={(sortData: any) => setSort(sortData)}
            onRowSelected={(rows: any) => setSelectedRows(rows.selectedRowKeys)}
            selectedRows={selectedRows}
            loading={loading || inactiveParticipantsListLoading}
            onPaginationChange={(currentPage: number) => setPage(currentPage)}
          />
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export default InviteParticipants
